import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useSearchParams } from "react-router-dom";

import { confiscationsListLimit, confiscationsListLimits } from "configs/limits";
import { useAppSelector, useAppDispatch } from "store/index";
import {
  getPlayerConfiscationsList,
  handleConfiscationsPaginate,
} from "store/players/list/slice";

import Pagination from "components/Pagination";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import EmptyState from "components/EmptyState/EmptyState";
import Tooltip from "components/Tooltip";
import { formatTime, useTimeFormat } from "helpers/timeFormat";
import {
  CONFISCATION_TYPE_LABELS,
  getCellText,
  truncateComment,
} from "./financeTabUtils";

const ListConfiscationsTab: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const timeFormat = useTimeFormat();

  const confiscationsList = useAppSelector(
    (store) => store.players.confiscationsList,
  );
  const totalItems = useAppSelector(
    (store) => store.players.totalItemsConfiscations,
  );
  const totalPages = useAppSelector(
    (store) => store.players.totalPagesConfiscations,
  );
  const activePage = useAppSelector(
    (store) => store.players.activePageConfiscations,
  );
  const isFetched = useAppSelector(
    (store) => store.players.isConfiscationsFetched,
  );

  const [limit, setLimit] = useState(confiscationsListLimit);
  const userId = searchParams.get("entityId");

  useEffect(() => {
    if (!userId) return;

    dispatch(
      getPlayerConfiscationsList({
        userId: Number(userId),
        limit,
        offset: activePage - 1,
      }),
    );
  }, [activePage, dispatch, limit, userId]);

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handleConfiscationsPaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handleConfiscationsPaginate(activePage + 1));
    }
  };

  const renderTooltipCell = ({
    display,
    title,
  }: {
    display: string;
    title?: string;
  }) => {
    if (!title) {
      return <span className="finance-cell-text">{display}</span>;
    }

    return (
      <Tooltip text={title} delay={120}>
        <span className="finance-cell-text">{display}</span>
      </Tooltip>
    );
  };

  return (
    <div className="player-content-wrapper">
      {!isFetched ? (
        <ModalTabSkeleton />
      ) : confiscationsList.length === 0 ? (
        <EmptyState message={t("noPlayerConfiscationsFound")} />
      ) : (
        <>
          <div className="inner-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t("type")}</th>
                  <th>{t("amount")}</th>
                  <th>Transaction Code</th>
                  <th>Internal Comment</th>
                  <th>{t("admin")}</th>
                  <th>{t("status")}</th>
                  <th>{t("created")}</th>
                </tr>
              </thead>

              <tbody>
                {confiscationsList.map((row) => {
                  const comment = truncateComment(row.internalComment);
                  const type = getCellText(
                    row.confiscationType
                      ? (CONFISCATION_TYPE_LABELS[row.confiscationType] ??
                          row.confiscationType)
                      : "—",
                  );
                  const transactionCode = getCellText(row.transactionCode);
                  const admin = getCellText(row.operatorName);
                  const status = getCellText(row.statusCode);

                  return (
                    <tr key={row.operationId}>
                      <td>{row.operationId}</td>
                      <td>{renderTooltipCell(type)}</td>
                      <td>{`${row.amount} ${row.currency}`}</td>
                      <td>{renderTooltipCell(transactionCode)}</td>
                      <td>
                        {comment.title ? (
                          <Tooltip text={comment.title} delay={120}>
                            <span className="finance-comment-cell">
                              {comment.display}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className="finance-comment-cell">
                            {comment.display}
                          </span>
                        )}
                      </td>
                      <td>{renderTooltipCell(admin)}</td>
                      <td>{renderTooltipCell(status)}</td>
                      <td className="date">
                        <span>
                          {moment(row.createdAt).format("DD.MM.YYYY")}
                        </span>
                        <span>{formatTime(row.createdAt, timeFormat)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {confiscationsList.length > 0 && (
            <Pagination
              limit={limit}
              activePage={activePage}
              totalItems={totalItems}
              totalPages={totalPages}
              limitsArray={confiscationsListLimits}
              handleLimitClick={(nextLimit) => {
                setLimit(nextLimit);
                dispatch(handleConfiscationsPaginate(1));
              }}
              handleNextClick={handleNextClick}
              handlePrevClick={handlePrevClick}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ListConfiscationsTab;
